import {
  useCallback,
  useDeferredValue,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Form } from 'antd';
import dayjs from 'dayjs';
import { withPrivateRoute } from '@/components/hoc/withPrivateRoute';
import { ContainerFluid, Section } from '@/components/layouts';
import RootLayout from '@/components/layouts/RootLayout';
import {
  FieldsSkeleton,
  ProfileDetailsPanel,
  ProfileErrorState,
  ProfileLeaveTileModal,
  ProfileTour,
  MiddleCollapseButton,
  MiddleCollapsedBar,
  ProfileTiles,
  TilesSkeleton,
  dropEmptyTiles,
  useEditLanding,
  tileEditable,
  tileFieldAccess,
  useMiddleCollapsed,
  useProfileGuide,
  useProfileTileSwitch,
} from '@/components/profile';
import { ROLES_PERMISSIONS, ROUTES } from '@/constants';
import { QueryParamsProvider } from '@/contexts/query-params/QueryParamsProvider';
import { useQueryParams } from '@/hooks';
import { useCompany } from '@/hooks/queries/use-company';
import { cn } from '@/libs/utils';
import { API } from '@/services/api.service';
import { COMPANY_FORM_DEFAULT_PARAMS } from '@/shared/company';
import { QUERY_KEY } from '@/shared/constants';
import CompanyGeneralDataForm from '../_components/form/CompanyGeneralDataForm';
import {
  COMPANY_PROFILE_FORM_SECTIONS,
  companySectionOfField,
  companyTileOfPermission,
} from '../_components/form/companyProfileSections';
import normalizeCompanyFormData from '../_components/form/normalizeCompanyFormData';
import CompanyProfileHeader from './_components/CompanyProfileHeader';
import CompanyProfileStats from './_components/CompanyProfileStats';
import {
  TILE_LABELS,
  buildCompanyTileGroups,
  companyTileSummary,
} from './_components/companyTiles';
import useCompanyPanelExtras from './_components/useCompanyPanelExtras';
import useCompanyProfileAccess from './useCompanyProfileAccess';

// Old ?route= tabs open the matching tile.
const ROUTE_TILES = {
  questionnaire: 'upitnik',
  'payment-instructions': 'placanje',
  'create-documents': 'dokumenta',
  'upload-documents': 'dokumenta',
};

// Tiles whose content is wide (the old tabs): the panel takes the middle's
// place, so nothing has to scroll sideways.
const WIDE_PANEL_TILES = [
  'kontakti',
  'zaposleni',
  'ugovori',
  'radnaMesta',
  'oprema',
  'placanje',
  'upitnik',
  'dokumenta',
  'sabloni',
  'istorija',
];

function PrivateCompanyProfile() {
  const { t } = useTranslation(['translations']);
  const tc = useCallback(
    (key, opts) => t(`companyProfile.${key}`, { ns: 'translations', ...opts }),
    [t]
  );
  const te = useCallback(
    (key, opts) => t(`employeeProfile.${key}`, { ns: 'translations', ...opts }),
    [t]
  );
  const { companyId } = useParams();
  const { queryParams } = useQueryParams();

  const { data: company, isLoading, error } = useCompany(companyId);
  const formData = useMemo(
    () => (company ? normalizeCompanyFormData(company) : null),
    [company]
  );

  const [form] = Form.useForm();
  // { save, discard } of the open tile, filled in by the form.
  const formActionsRef = useRef(null);
  const [year, setYear] = useState(dayjs().year());
  const [selected, setSelected] = useState(
    ROUTE_TILES[queryParams.route] || queryParams.tile || 'osnovno'
  );
  const [editing, setEditing] = useState(queryParams.mode === 'edit');
  const [dirtySections, setDirtySections] = useState([]);
  const handleDirtySections = useCallback(
    (next) =>
      setDirtySections((prev) => (prev.join() === next.join() ? prev : next)),
    []
  );

  const access = useCompanyProfileAccess();
  const isEditing = editing && access.canEdit;

  // Company statistics; roles without them (employee, e-invoice officer) get
  // a 403 and the page simply has no middle column.
  const {
    data: stats,
    isLoading: statsLoading,
    error: statsErrorObj,
  } = useQuery({
    queryKey: [QUERY_KEY.company, companyId, 'profile-stats', year],
    queryFn: () => API.companies.getCompanyProfileStats(companyId, year),
    enabled: !!companyId,
    retry: false,
  });
  const statsForbidden = statsErrorObj?.response?.status === 403;
  const showStats = !statsForbidden;

  const extras = useCompanyPanelExtras({
    company,
    companyId,
    formData,
    stats,
    access,
  });

  // What the role may see and change on each tile, by the server's map.
  const fieldAccess = useMemo(
    () =>
      tileFieldAccess(
        company?.COMPANY_GENERAL_DATA_FORM_FIELDS,
        companyTileOfPermission
      ),
    [company]
  );

  // Tiles with content of their own besides the form (a list, a tab).
  const ownContent = Object.keys(extras.after)
    .filter((id) => extras.before[id] || extras.after[id])
    .join();

  const groups = useMemo(() => {
    if (!company) return [];
    return dropEmptyTiles(
      buildCompanyTileGroups({
        company,
        can: access,
        showStats,
        showEquipment: extras.showEquipment,
      }),
      fieldAccess,
      (id) => ownContent.split(',').includes(id)
    ).map((g) => ({ ...g, title: tc(g.label) }));
  }, [
    company,
    access,
    showStats,
    extras.showEquipment,
    tc,
    fieldAccess,
    ownContent,
  ]);

  const allTiles = groups.flatMap((g) => g.tiles);
  const activeTile = allTiles.includes(selected)
    ? selected
    : allTiles[0] || 'osnovno';

  // With e-invoicing off, the e-invoice tile shows only its main switch.
  const eInvoiceSwitch =
    company?.COMPANY_GENERAL_DATA_FORM_FIELDS?.['applicationSettings.eInvoice'];
  const canEditTile = (id) =>
    !!formData &&
    access.canEdit &&
    COMPANY_PROFILE_FORM_SECTIONS.includes(id) &&
    tileEditable(fieldAccess, id) &&
    (id !== 'efakture' ||
      !!company?.applicationSettings?.eInvoice ||
      (!!eInvoiceSwitch?.visibility && !eInvoiceSwitch?.disabled));

  useEditLanding({
    editing,
    setEditing,
    tiles: company && formData ? allTiles : [],
    activeTile,
    canEditTile,
    setSelected,
  });

  const renderTile = (id) => ({
    label: tc(TILE_LABELS[id]),
    dirty: isEditing && dirtySections.includes(id),
    dirtyLabel: te('unsavedTile'),
    ...companyTileSummary(id, {
      company,
      stats,
      tc,
      te,
      t,
      team: extras.team,
      jobPositions: extras.jobPositions,
      equipment: extras.equipment,
      clientContacts: extras.clientContacts,
    }),
  });

  // Stable, so the memoized form is not re-rendered by a new function.
  const stopEditing = useCallback(() => {
    setEditing(false);
    setDirtySections([]);
  }, [setEditing]);
  const handleFinishFailed = useCallback(({ errorFields }) => {
    const first = errorFields?.[0]?.name;
    if (first) setSelected(companySectionOfField(first));
  }, [setSelected]);

  // Layout guide: not over unsaved changes. It opens the tile's edit mode
  // itself to show Save, and closes it again.
  const guide = useProfileGuide({
    kind: 'company',
    ready: !!company && !!formData,
    blocked: isEditing && dirtySections.length > 0,
    isEditing,
    startEditing: () => setEditing(true),
    stopEditing,
  });

  const { selectTile, leavePrompt } = useProfileTileSwitch({
    isEditing,
    dirtySections,
    setSelected,
    stopEditing,
    formActionsRef,
    activeTile,
  });

  const errorStatus = error?.response?.status;
  const errorMessage =
    errorStatus === 404
      ? tc('errorNotFound')
      : errorStatus === 403
        ? tc('errorForbidden')
        : te('errorGeneric');

  // The tile is marked at once; the panel (the whole form re-renders on a
  // tile change) follows in a background render that does not hold the click.
  const shownTile = useDeferredValue(activeTile);
  const isSwitching = shownTile !== activeTile;

  const showWidePanel = !showStats || WIDE_PANEL_TILES.includes(shownTile);
  // The statistics column can be folded down to a bar.
  // The statistics column starts folded on a company (Bora 17.10: unclear what
  // it is for); the arrow opens it and the choice is remembered.
  const [middleCollapsed, setMiddleCollapsed] = useMiddleCollapsed({ defaultCollapsed: true, storageKey: 'profile:company:middleCollapsed' });
  const middleFolded = !showWidePanel && middleCollapsed;

  return (
    <RootLayout metadata={{ title: error ? errorMessage : company?.name }}>
      <Section>
        <ContainerFluid className="foreground-wrapper !py-3">
          <div className="employee-profile flex h-[calc(100svh-104px)] min-h-[640px] flex-col overflow-hidden rounded-[10px] border-4 border-accent bg-accent">
            <CompanyProfileHeader
              company={company}
              companyId={companyId}
              isLoading={isLoading}
              hasError={!!error}
              onOpenGuide={guide.start}
            />

            {error ? (
              <ProfileErrorState
                message={errorMessage}
                detail={
                  errorStatus !== 404 && errorStatus !== 403
                    ? error?.response?.data?.message
                    : null
                }
                backUrl={`${ROUTES.admin}/${ROUTES.companies}`}
                backLabel={te('backToList')}
              />
            ) : (
              <div
                className={cn(
                  'grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)] gap-3 p-3',
                  showWidePanel
                    ? 'grid-cols-[380px_minmax(0,1fr)]'
                    : middleFolded
                      ? 'grid-cols-[380px_14px_minmax(0,1fr)]'
                      : 'grid-cols-[380px_minmax(0,1fr)_560px]'
                )}
              >
                <div className="flex min-h-0 flex-col">
                  {company ? (
                    <ProfileTiles
                      label={company.name}
                      groups={groups}
                      selected={activeTile}
                      onSelect={selectTile}
                      renderTile={renderTile}
                    />
                  ) : (
                    <TilesSkeleton />
                  )}
                </div>

                {!showWidePanel &&
                  (middleFolded ? (
                    <MiddleCollapsedBar
                      onExpand={() => setMiddleCollapsed(false)}
                    />
                  ) : (
                    <div
                      data-tour="profile-middle"
                      className="relative flex min-h-0 min-w-0 flex-col"
                    >
                      <MiddleCollapseButton
                        onCollapse={() => setMiddleCollapsed(true)}
                      />
                      <CompanyProfileStats
                        company={company}
                        stats={stats}
                        statsLoading={statsLoading || isLoading}
                        statsError={!!statsErrorObj && !statsForbidden}
                        year={year}
                        onYearChange={setYear}
                        selected={shownTile}
                      />
                    </div>
                  ))}

                <ProfileDetailsPanel
                  title={tc(TILE_LABELS[shownTile])}
                  isEditing={isEditing}
                  onEdit={
                    canEditTile(shownTile) ? () => setEditing(true) : undefined
                  }
                  dirtyLabels={dirtySections
                    .filter((id) => TILE_LABELS[id])
                    .map((id) => tc(TILE_LABELS[id]))}
                  before={extras.before[shownTile]}
                  after={extras.after[shownTile]}
                  className={cn(
                    showWidePanel && 'employee-profile__panel--wide',
                    'transition-opacity',
                    isSwitching && 'opacity-60'
                  )}
                >
                  {formData ? (
                    <CompanyGeneralDataForm
                      formData={formData}
                      isEdited
                      companyId={companyId}
                      form={form}
                      profileSection={shownTile}
                      readOnly={!isEditing}
                      onSaved={stopEditing}
                      onCancel={stopEditing}
                      onFinishFailed={handleFinishFailed}
                      onDirtySectionsChange={handleDirtySections}
                      actionsRef={formActionsRef}
                      className="!px-3.5 !py-3"
                    />
                  ) : (
                    <FieldsSkeleton />
                  )}
                </ProfileDetailsPanel>
              </div>
            )}
          </div>
        </ContainerFluid>
      </Section>

      <ProfileTour
        kind="company"
        steps={guide.steps}
        current={guide.current}
        onChange={guide.change}
        onClose={guide.close}
      />

      <ProfileLeaveTileModal
        {...leavePrompt}
        tileLabel={tc(TILE_LABELS[activeTile])}
      />
    </RootLayout>
  );
}

function CompanyProfileWithParams() {
  return (
    <QueryParamsProvider defaultParams={COMPANY_FORM_DEFAULT_PARAMS}>
      <PrivateCompanyProfile />
    </QueryParamsProvider>
  );
}

const CompanyProfile = withPrivateRoute(
  CompanyProfileWithParams,
  ROLES_PERMISSIONS.COMPANIES.VIEW
);

export default CompanyProfile;
